// Design tokens — ported verbatim from design/theme.jsx so the visual output matches pixel-perfect.
export const PRIMARY_PRESETS = {
  blue:   { 600: "#2563EB", 700: "#1D4ED8", 100: "#DBEAFE", 50: "#EFF6FF", 900: "#1E3A8A", 400: "#60A5FA" },
  purple: { 600: "#6E6FCF", 700: "#5A5BB8", 100: "#E3E3F5", 50: "#F3F3FB", 900: "#3A3B8C", 400: "#9A9BE0" },
} as const;

export const FONT_PRESETS = {
  pretendard: "'Pretendard', 'Pretendard Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  inter:      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  system:     "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
} as const;

export const DENSITY_PRESETS = {
  compact:     { row: 36, pad: 10, gap: 8,  fs: 13, top: 56 },
  normal:      { row: 44, pad: 14, gap: 12, fs: 14, top: 64 },
  comfortable: { row: 52, pad: 18, gap: 16, fs: 15, top: 72 },
} as const;

export type ThemeState = {
  primary: keyof typeof PRIMARY_PRESETS;
  density: keyof typeof DENSITY_PRESETS;
  dark: boolean;
  font: keyof typeof FONT_PRESETS;
  sidebarWidth: number;
};

export const DEFAULT_THEME: ThemeState = {
  primary: "blue",
  density: "normal",
  dark: false,
  font: "pretendard",
  sidebarWidth: 240,
};

export function applyTheme(t: ThemeState): void {
  const r = document.documentElement;
  const p = PRIMARY_PRESETS[t.primary] || PRIMARY_PRESETS.blue;
  const d = DENSITY_PRESETS[t.density] || DENSITY_PRESETS.normal;

  r.style.setProperty("--p-600", p[600]);
  r.style.setProperty("--p-700", p[700]);
  r.style.setProperty("--p-400", p[400]);
  r.style.setProperty("--p-100", p[100]);
  r.style.setProperty("--p-50",  p[50]);
  r.style.setProperty("--p-900", p[900]);

  // Module accents
  r.style.setProperty("--m-dashboard", p[600]);
  r.style.setProperty("--m-collab",   "#0891B2");
  r.style.setProperty("--m-task",     "#7C3AED");
  r.style.setProperty("--m-approval", "#D97706");
  r.style.setProperty("--m-hrm",      "#16A34A");
  r.style.setProperty("--m-finance",  "#0F766E");
  r.style.setProperty("--m-crm",      "#DB2777");
  r.style.setProperty("--m-bi",       "#A16207");
  r.style.setProperty("--m-system",   "#475569");

  if (t.dark) {
    r.style.setProperty("--bg",      "#0B1220");
    r.style.setProperty("--bg-0",    "#111A2E");
    r.style.setProperty("--bg-1",    "#0B1220");
    r.style.setProperty("--surface", "#111A2E");
    r.style.setProperty("--panel",   "#16223B");
    r.style.setProperty("--elev",    "#1B2945");
    r.style.setProperty("--border",  "#2A3656");
    r.style.setProperty("--border-strong", "#3B4A75");
    r.style.setProperty("--text",    "#E2E8F0");
    r.style.setProperty("--text-2",  "#B5C0D5");
    r.style.setProperty("--text-3",  "#7A8AA8");
    r.style.setProperty("--hover",   "rgba(255,255,255,.045)");
    r.style.setProperty("--active",  "rgba(110,111,207,.18)");
    r.style.setProperty("--p-tint",  "rgba(110,111,207,.18)");
    r.style.setProperty("--shadow-sm", "0 1px 2px rgba(0,0,0,.45)");
    r.style.setProperty("--shadow-md", "0 6px 18px rgba(0,0,0,.45)");
    r.style.setProperty("--ok",      "#22C55E");
    r.style.setProperty("--warn",    "#F59E0B");
    r.style.setProperty("--danger",  "#EF4444");
    r.style.setProperty("--info",    "#06B6D4");
    r.dataset.dark = "true";
  } else {
    r.style.setProperty("--bg",      "#F6F7F9");
    r.style.setProperty("--bg-0",    "#FFFFFF");
    r.style.setProperty("--bg-1",    "#F6F7F9");
    r.style.setProperty("--surface", "#FFFFFF");
    r.style.setProperty("--panel",   "#F3F4F6");
    r.style.setProperty("--elev",    "#FFFFFF");
    r.style.setProperty("--border",  "#E5E7EB");
    r.style.setProperty("--border-strong", "#D1D5DB");
    r.style.setProperty("--text",    "#111827");
    r.style.setProperty("--text-2",  "#374151");
    r.style.setProperty("--text-3",  "#6B7280");
    r.style.setProperty("--hover",   "rgba(17,24,39,.045)");
    r.style.setProperty("--active",  p[100]);
    r.style.setProperty("--p-tint",  p[50]);
    r.style.setProperty("--shadow-sm", "0 1px 2px rgba(15,23,42,.06)");
    r.style.setProperty("--shadow-md", "0 8px 24px rgba(15,23,42,.10)");
    r.style.setProperty("--ok",      "#16A34A");
    r.style.setProperty("--warn",    "#D97706");
    r.style.setProperty("--danger",  "#DC2626");
    r.style.setProperty("--info",    "#0891B2");
    r.dataset.dark = "false";
  }

  r.style.setProperty("--row", d.row + "px");
  r.style.setProperty("--pad", d.pad + "px");
  r.style.setProperty("--gap", d.gap + "px");
  r.style.setProperty("--fs",  d.fs + "px");
  r.style.setProperty("--top", d.top + "px");
  r.style.setProperty("--sb",  (t.sidebarWidth || 240) + "px");
  r.style.setProperty("--app-font", FONT_PRESETS[t.font] || FONT_PRESETS.pretendard);
}
